import { useEffect, useState } from 'react';
import { resetTracking } from '@/services/tracking';
import { useNavigate } from 'react-router-dom';
import { useEndImpersonation } from '@/hooks/useAdmin';
import { useAuth } from '@/hooks/useAuth';
import { setAccessToken } from '@/services/api';
import { toast } from '@/utils/toast';
import { Tooltip } from '@/components/ui/Tooltip';
import { devLog } from '@/utils/logger';

/**
 * ImpersonationBanner Component
 *
 * Displays a compact inline pill when a super admin is impersonating a user.
 * Uses JWT-based detection instead of API polling for better accuracy.
 *
 * Shows:
 * - Target user email (truncated)
 * - Read-only mode indicator (if applicable)
 * - Time remaining until session expires
 * - Exit button to end impersonation
 *
 * Design:
 * - Inline pill component (sits in navbar)
 * - Red background (#ef4444 / bg-red-500)
 * - White text
 * - Similar to GodModeIndicator but with red color scheme
 */

/**
 * Calculate time remaining from ISO timestamp
 * Returns compact formatted string like "25m" or "2h 15m"
 */
const getTimeRemaining = (expiresAt: string): string => {
  const now = new Date().getTime();
  const expires = new Date(expiresAt).getTime();
  const msRemaining = expires - now;

  if (msRemaining <= 0) {
    return 'Expired';
  }

  const minutesRemaining = Math.floor(msRemaining / 60000);
  const hoursRemaining = Math.floor(minutesRemaining / 60);

  if (hoursRemaining > 0) {
    const remainingMinutes = minutesRemaining % 60;
    return remainingMinutes > 0
      ? `${hoursRemaining}h ${remainingMinutes}m`
      : `${hoursRemaining}h`;
  }

  return `${minutesRemaining}m`;
};

export const ImpersonationBanner = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const endImpersonation = useEndImpersonation();
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  // Get session from auth context (JWT-based detection)
  const session = user?.impersonationSession;

  // Update countdown timer every minute
  useEffect(() => {
    if (!session) {
      return;
    }

    // Initial calculation
    setTimeRemaining(getTimeRemaining(session.expiresAt));

    // Update every 60 seconds
    const interval = setInterval(() => {
      setTimeRemaining(getTimeRemaining(session.expiresAt));
    }, 60000);

    return () => clearInterval(interval);
  }, [session]);

  // Don't render if no active session
  if (!session) {
    return null;
  }

  /**
   * Handle exit button click
   * Calls endImpersonation mutation, restores admin tokens, and navigates
   */
  const handleExit = async () => {
    if (!session) return;

    try {
      const result = await endImpersonation.mutateAsync(session.id);

      // Set admin's access token in memory
      await resetTracking();
      setAccessToken(result.accessToken);

      // Refresh user context to clear impersonation session
      await refreshUser();

      toast.success('Impersonation session ended');

      // Navigate to admin dashboard (SPA navigation)
      navigate('/admin');
    } catch (error) {
      devLog.error('[ImpersonationBanner] Failed to end impersonation:', error);
      toast.error('Failed to end impersonation session');
    }
  };

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 bg-red-500 text-white rounded-lg shadow-md max-w-full"
      role="alert"
      aria-live="polite"
    >
      {/* Warning icon */}
      <svg
        className="w-4 h-4 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>

      {/* Text - truncate long emails */}
      <Tooltip content={session.targetEmail}>
        <span className="text-xs font-medium truncate max-w-[120px] sm:max-w-[180px]">
          <span className="hidden sm:inline">Impersonating: </span>
          <strong className="font-bold">{session.targetEmail}</strong>
        </span>
      </Tooltip>

      {/* Read-only badge (if applicable) */}
      {session.isReadOnly && (
        <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 bg-red-700 rounded text-[10px] font-semibold">
          RO
        </span>
      )}

      {/* Time remaining - compact */}
      <Tooltip content={`Expires in ${timeRemaining}`}>
        <span className="flex-shrink-0 text-[10px] text-red-100">
          {timeRemaining}
        </span>
      </Tooltip>

      {/* Exit button - compact */}
      <button
        onClick={handleExit}
        disabled={endImpersonation.isPending}
        className="flex-shrink-0 px-2 py-1 bg-red-700 text-white rounded-md text-xs font-semibold hover:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Exit impersonation session"
      >
        {endImpersonation.isPending ? '...' : 'Exit'}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
